import { useMemo, useState } from "react";
import { toast } from "sonner";
import client, { assetUrl, API } from "@/lib/glossyApi";
import { CalendarClock, Sparkles, X, CheckCircle2 } from "lucide-react";

const absUrl = (path) => (path?.startsWith("http") ? path : `${API}/files/${path}`);
// Optimum UK posting windows by weekday (0=Sun)
const SMART = { 0: [19, 0], 1: [12, 0], 2: [7, 30], 3: [7, 30], 4: [7, 30], 5: [12, 0], 6: [11, 0] };
const SLOT_LABEL = { 0: "7 PM — Sunday planning scroll", 1: "12 PM — lunch break", 2: "7:30 AM — commute", 3: "7:30 AM — commute", 4: "7:30 AM — commute", 5: "12 PM — lunch break", 6: "11 AM — weekend browse" };

const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const smartTime = (day) => {
  const [h, m] = SMART[day.getDay()];
  const t = new Date(day);
  t.setHours(h, m, 0, 0);
  if (t < new Date()) { // slot already passed today — push to the next sensible hour
    const n = new Date(); n.setMinutes(0, 0, 0); n.setHours(n.getHours() + 1);
    return n;
  }
  return t;
};

export const Planner = ({ posts, settings, onChanged }) => {
  const [picking, setPicking] = useState(null); // dayKey being filled
  const [busy, setBusy] = useState(false);

  const days = useMemo(() => {
    const out = [];
    const start = new Date(); start.setHours(0, 0, 0, 0);
    for (let i = 0; i < 14; i++) { const d = new Date(start); d.setDate(d.getDate() + i); out.push(d); }
    return out;
  }, []);

  const byDay = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      const when = p.status === "scheduled" ? p.schedule_at : p.posted_instagram ? p.posted_at : null;
      if (!when) return;
      const k = dayKey(new Date(when));
      (map[k] = map[k] || []).push(p);
    });
    Object.values(map).forEach((l) => l.sort((a, b) => new Date(a.schedule_at || a.posted_at) - new Date(b.schedule_at || b.posted_at)));
    return map;
  }, [posts]);

  const drafts = posts.filter((p) => p.status !== "scheduled" && !p.posted_instagram && p.image && (!p.platforms || p.platforms.includes("instagram")));

  const scheduleInto = async (p, day) => {
    if (!settings?.instagram_connected) { toast.error("Connect Instagram first — scheduled posts publish automatically."); return; }
    setBusy(true);
    try {
      const t = smartTime(day);
      await client.patch(`/admin/social/${p.id}/schedule`, {
        schedule_at: t.toISOString(),
        image_url: absUrl(p.image),
        caption: [p.caption, (p.hashtags || []).join(" ")].filter(Boolean).join("\n\n"),
      });
      toast.success(`Booked for ${t.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} — the optimum window that day.`);
      setPicking(null); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not schedule."); }
    setBusy(false);
  };

  const fillAllGaps = async () => {
    if (!settings?.instagram_connected) { toast.error("Connect Instagram first."); return; }
    const gaps = days.filter((d) => !(byDay[dayKey(d)] || []).length && smartTime(d) > new Date());
    if (!drafts.length) { toast.error("No unscheduled drafts to place — design some in the Campaign Composer first."); return; }
    setBusy(true);
    let placed = 0;
    for (let i = 0; i < gaps.length && i < drafts.length; i++) {
      const p = drafts[i]; const t = smartTime(gaps[i]);
      try {
        await client.patch(`/admin/social/${p.id}/schedule`, {
          schedule_at: t.toISOString(), image_url: absUrl(p.image),
          caption: [p.caption, (p.hashtags || []).join(" ")].filter(Boolean).join("\n\n"),
        });
        placed++;
      } catch { break; }
    }
    toast.success(placed ? `${placed} draft${placed > 1 ? "s" : ""} placed into the best slot of each empty day.` : "Nothing placed.");
    setBusy(false); onChanged?.();
  };

  const unschedule = async (p) => {
    await client.patch(`/admin/social/${p.id}/schedule`, { schedule_at: null });
    toast.success("Taken off the calendar — back to drafts.");
    onChanged?.();
  };

  const today = dayKey(new Date());

  return (
    <div data-testid="planner">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="font-sans text-sm text-ink/50">The next 14 days — one edition cycle. Empty days show the optimum UK posting window; fill them and everything publishes itself.</p>
        <button onClick={fillAllGaps} disabled={busy} className="overline px-4 py-2.5 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors flex items-center gap-2 disabled:opacity-60" data-testid="fill-gaps-btn">
          <Sparkles size={14} /> Fill every gap with drafts
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 border-t border-l border-ink/15" data-testid="planner-grid">
        {days.map((d) => {
          const k = dayKey(d);
          const items = byDay[k] || [];
          const isToday = k === today;
          const slotPassed = smartTime(d) <= new Date() && !items.length;
          return (
            <div key={k} className={`border-r border-b border-ink/15 p-3 min-h-[150px] flex flex-col ${isToday ? "bg-gloss-gold/10" : items.length ? "bg-white" : "bg-secondary/40"}`} data-testid={`planner-day-${k}`}>
              <p className="overline text-[10px] text-ink/45 mb-2">
                {d.toLocaleDateString("en-GB", { weekday: "short" })} <span className="text-ink">{d.getDate()}</span> {d.toLocaleDateString("en-GB", { month: "short" })}{isToday ? " · today" : ""}
              </p>
              <div className="flex-1 space-y-2">
                {items.map((p) => (
                  <div key={p.id} className="flex items-start gap-2 group" data-testid={`planner-item-${p.id}`}>
                    {assetUrl(p.image) && <img src={assetUrl(p.image)} alt="" className="w-9 h-12 object-cover shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className={`font-mono text-[10px] flex items-center gap-1 ${p.posted_instagram ? "text-green-700" : p.status === "schedule_failed" ? "text-red-600" : "text-ink/70"}`}>
                        {p.posted_instagram ? <CheckCircle2 size={10} /> : <CalendarClock size={10} />}
                        {new Date(p.schedule_at || p.posted_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                        {p.posted_instagram ? " live" : p.status === "schedule_failed" ? " failed" : ""}
                      </p>
                      <p className="font-sans text-[11px] text-ink/60 leading-tight line-clamp-2">{p.caption || p.post_type}</p>
                    </div>
                    {!p.posted_instagram && (
                      <button onClick={() => unschedule(p)} className="opacity-0 group-hover:opacity-100 text-ink/40 hover:text-red-500 transition-opacity shrink-0" title="Unschedule" data-testid={`planner-unschedule-${p.id}`}><X size={12} /></button>
                    )}
                  </div>
                ))}
                {!items.length && !slotPassed && (
                  <div>
                    <p className="font-sans text-[10px] text-ink/40 leading-snug mb-2">
                      {(() => { const [h, m] = SMART[d.getDay()]; const w = new Date(d); w.setHours(h, m, 0, 0); return w <= new Date() ? "Window passed — books the next hour" : `Best slot · ${SLOT_LABEL[d.getDay()]}`; })()}
                    </p>
                    <button onClick={() => setPicking(picking === k ? null : k)} className="overline text-[10px] border border-ink/25 px-2.5 py-1.5 hover:bg-ink hover:text-cream transition-colors" data-testid={`planner-fill-${k}`}>+ Fill</button>
                  </div>
                )}
              </div>
              {picking === k && (
                <div className="mt-2 border-t border-ink/10 pt-2 space-y-1.5 max-h-44 overflow-y-auto" data-testid={`planner-picker-${k}`}>
                  {drafts.length === 0 && <p className="font-sans text-[10px] text-ink/45">No drafts ready — design one in the Campaign Composer.</p>}
                  {drafts.map((p) => (
                    <button key={p.id} onClick={() => scheduleInto(p, d)} disabled={busy} className="w-full flex items-center gap-2 text-left hover:bg-secondary p-1 transition-colors disabled:opacity-50" data-testid={`planner-pick-${p.id}`}>
                      {assetUrl(p.image) && <img src={assetUrl(p.image)} alt="" className="w-7 h-9 object-cover shrink-0" />}
                      <span className="font-sans text-[10px] text-ink/70 line-clamp-2">{p.caption || p.post_type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
